import type { SupportedStorage } from "@supabase/supabase-js";

/**
 * Detects if we're running inside a Capacitor native context.
 * Falls back gracefully to localStorage on web.
 */
function isCapacitorNative(): boolean {
  return (
    typeof window !== "undefined" &&
    // Capacitor sets window.Capacitor when running natively
    "Capacitor" in window &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as unknown as { Capacitor: { isNativePlatform: () => boolean } })
      .Capacitor.isNativePlatform()
  );
}

/**
 * Lazy-loaded Capacitor Preferences adapter.
 * We import dynamically to avoid crashing in browser environments where
 * Capacitor native plugins aren't available.
 */
async function getPreferences(): Promise<{
  get: (opts: { key: string }) => Promise<{ value: string | null }>;
  set: (opts: { key: string; value: string }) => Promise<void>;
  remove: (opts: { key: string }) => Promise<void>;
} | null> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    return Preferences;
  } catch {
    return null;
  }
}

/**
 * Creates a SupportedStorage adapter that uses:
 * - @capacitor/preferences on native iOS/Android (secure, persistent)
 * - localStorage on web (browser default)
 */
export function createCapacitorStorage(): SupportedStorage {
  const native = isCapacitorNative();

  if (!native) {
    // Web fallback — use localStorage directly
    return {
      getItem: (key: string): string | null => {
        try {
          return localStorage.getItem(key);
        } catch {
          return null;
        }
      },
      setItem: (key: string, value: string): void => {
        try {
          localStorage.setItem(key, value);
        } catch {
          // Quota exceeded or private mode — ignore
        }
      },
      removeItem: (key: string): void => {
        try {
          localStorage.removeItem(key);
        } catch {
          // Ignore
        }
      },
    };
  }

  // Native adapter — uses Capacitor Preferences (async, but Supabase accepts thenable)
  return {
    getItem: async (key: string): Promise<string | null> => {
      const prefs = await getPreferences();
      if (!prefs) return null;
      const result = await prefs.get({ key });
      return result.value;
    },
    setItem: async (key: string, value: string): Promise<void> => {
      const prefs = await getPreferences();
      if (!prefs) return;
      await prefs.set({ key, value });
    },
    removeItem: async (key: string): Promise<void> => {
      const prefs = await getPreferences();
      if (!prefs) return;
      await prefs.remove({ key });
    },
  };
}
